import type { ListingSource, OwnershipCheckSession } from '@/types/listing';

/**
 * Odznaka osi WŁASNOŚCI (czy to jego mieszkanie), pokazywana na /owner/listing.
 * Wartości identyczne jak w `IdentityBadge` z identity.ts (oś TOŻSAMOŚCI) — patrz
 * tam po powód, dla którego nie są to typy nominalne, i po regułę nazewniczą.
 */
export type OwnershipBadge = 'unverified' | 'pending' | 'verified' | 'declined';

/**
 * Pure mapping from the latest ownership check session to a badge state.
 * `in_progress`/`in_review`/`pending` all read as "pending" to the owner; only a
 * terminal `approved` is "verified". No session yet → "unverified".
 */
export function deriveOwnershipBadge(session: OwnershipCheckSession | null): OwnershipBadge {
  if (!session) return 'unverified';
  switch (session.status) {
    case 'approved':
      return 'verified';
    case 'declined':
      return 'declined';
    case 'pending':
    case 'in_progress':
    case 'in_review':
      return 'pending';
    default:
      return 'unverified';
  }
}

/** Whether a verification run is still in flight (drives status polling). */
export function isOwnershipInFlight(session: OwnershipCheckSession | null): boolean {
  if (!session) return false;
  return (
    session.status === 'in_progress' ||
    session.status === 'in_review' ||
    (session.status === 'pending' && !!session.external_id)
  );
}

/**
 * Owner-only verification nudge. Claim-sourced listings: the public badge and
 * owner-wins are gated on verification. Manual listings (multi-origin supply):
 * they are PUBLIC from publish, so verification now buys the "Zweryfikowany
 * właściciel" badge, clears the "owner is completing verification" note on the
 * public contact card, and unlocks replying to tenant messages — plenty to
 * nudge about. A pending run skips it either way (no nagging mid-verification).
 */
export function needsOwnershipNudge(
  source: ListingSource | null | undefined,
  badge: OwnershipBadge,
): boolean {
  if (!source) return false;
  return badge === 'unverified' || badge === 'declined';
}
